import { lazy, Suspense, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Routes, Route } from 'react-router-dom';
import LoadingBar from 'react-top-loading-bar';
import { useQuery } from '@tanstack/react-query';

import './App.css';
import useLoadingBar from './hooks/useLoadingBar';
import Header from './components/header';
import Home from './pages/home';
import ServicesStatus from './pages/status';
import Footer from './components/footer';
import FullPageSpinner from './components/fullpage-spinner';
import ScrollToTop from './components/scroll-to-top';
import { useUserUpdateListener } from './hooks/useUserUpdateListener';
import { getServicesStatus } from './actions/status';
import { useGTMPageView } from './hooks/useGTMPageView';
import { stripLanguageFromPath } from './utils/languageUrl';

const Unauthorized = lazy(() => import('./pages/unauthorized'));
const ErrorPage = lazy(() => import('./pages/error'));

function App() {
  const {
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    getAccessTokenSilently,
  } = useAuth0();
  const loadingRef = useLoadingBar();

  useQuery(['status'], getServicesStatus, {
    refetchInterval: 120000,
    refetchOnWindowFocus: false,
    retry: 3,
  });
  useUserUpdateListener();
  useEffect(() => {
    const checkSession = async () => {
      try {
        if (!isAuthenticated && !isLoading) {
          await getAccessTokenSilently({
            authorizationParams: {
              audience: process.env.REACT_APP_AUTH0_AUDIENCE,
              scope: 'email profile user_metadata company:details',
            },
          });
        }
      } catch (error) {
        console.error('Error retrieving token or session: ', error);
      }
    };

    checkSession();
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);

  useGTMPageView();

  const pathnameWithoutLanguage = stripLanguageFromPath(window.location.pathname);

  if (!isAuthenticated && pathnameWithoutLanguage === '/ciam-continue') {
    loginWithRedirect();
    return <FullPageSpinner />;
  }

  const shouldRenderFooter = !pathnameWithoutLanguage.includes('/status');

  return (
    <div id="home" className="relative">
      <>
        <LoadingBar color="#f11946" ref={loadingRef} />
        <Suspense fallback={<FullPageSpinner />}>
          <Header />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/:lang" element={<Home />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="/:lang/unauthorized" element={<Unauthorized />} />
            <Route path="/status" element={<ServicesStatus />} />
            <Route path="/:lang/status" element={<ServicesStatus />} />
            <Route path="*" element={<ErrorPage />} />
          </Routes>
          <ScrollToTop />
        </Suspense>
        {shouldRenderFooter && <Footer />}
      </>
    </div>
  );
}

export default App;
